<template>
  <div
    :class="[
      wrapperClass,
      'input-component',
      { 'd-flex align-items-center': formField.inlineLabel },
    ]"
    v-if="!formField.displayOnly"
    :data-cy="formField.name"
  >
    <label
      v-if="(formField.label || formField.name) && !formField.hideLabel"
      :for="formField.name"
      class="font-weight-bold text-capitalize"
      :class="[
        incorrectInputValues ? 'text-danger' : 'text-muted',
        { 'mr-4': formField.inlineLabel && formField.options },
      ]"
    >
      {{ formField.label || formField.name.replaceAll("_", " ") }}
      <span v-if="formField.required" v-c-tooltip="'This field is required'"
        >*</span
      >
    </label>
    <small
      v-if="
        formField.guidanceText &&
        incorrectInputValues &&
        formField.guidanceCondition(formField.value)
      "
      class="text-danger"
    >
      <br />
      {{ formField.guidanceText }}
    </small>
    <MzSelect
      v-if="formField.type === 'select'"
      class="mb-0 ra-input w-100"
      is-filter
      size="sm"
      :name="formField.name"
      :modelValue="formField.value"
      :options="
        formField.options.map((o) => ({
          value: o.value || o,
          label: o.label || o.name || o,
        }))
      "
      :disabled="disabled || formField.disabled"
      :searchable="formField.searchable"
      options-placement="bottom"
      @update:modelValue="(value) => selectHandler({ value }, formField.name)"
      :append-options-to-body="appendDropdownToBody"
    >
      <template v-for="slot in Object.keys($slots)" #[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
    </MzSelect>
    <MzSelect
      v-if="formField.type === 'multiselect'"
      :placeholder="formField.placeholder"
      searchable
      filterInput
      class="ra-input w-100"
      multiple
      size="sm"
      :taggable="formField.taggable"
      :options="
        formField.options.map((o) => ({
          value: o.value || o,
          label: o.label || o.name || o,
        }))
      "
      :disabled="disabled || formField.disabled"
      v-model="formField.value"
      @update:modelValue="(value) => selectHandler({ value }, formField.name)"
      :append-options-to-body="appendDropdownToBody"
    >
      <template v-for="slot in Object.keys($slots)" #[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
    </MzSelect>
    <RaInput
      size="sm"
      class="filter-input"
      :date-format="formField?.dateFormat"
      :name="formField.name"
      v-model="formField.value"
      @input="(e) => formField.validation?.(e)"
      v-if="['date', 'password', 'text', 'number'].includes(formField.type)"
      :type="formField.type"
      :disabled="disabled || formField.disabled"
      :autocomplete="formField.autocomplete"
      :append-dropdown-to-body="appendDropdownToBody"
      v-bind="formField.additionalProps"
      show-month-by-default
    />
    <CInputRadioGroup
      :name="formField.name"
      v-if="formField.type === 'radio'"
      :options="formField.options"
      v-model:checked="formField.value"
      :disabled="disabled || formField.disabled"
      inline
    />
    <CInputCheckboxGroup
      :name="formField.name"
      v-if="formField.type === 'checkbox'"
      :options="formField.options"
      v-model:checked="formField.value"
      :disabled="disabled || formField.disabled"
      inline
    />
    <div :name="formField.name" v-if="formField.type === 'textarea'">
      <FileManagement
        v-if="formField.file && !disabled && !formField.disabled"
        :file-id="formField.file.id"
        :upload-query="formField.file.query || {}"
        :disabled="formField.disabled || formField.file.disabled"
        @use="(filename) => useFilename(filename)"
      />
      <RaTextEditor
        v-if="!disabled && !formField.disabled"
        v-model="formField.value"
        :disabled="disabled || formField.disabled"
        :key="`ck-${formField.name}`"
        allow-image
        @click="handleLinksForDownload($event)"
      />
      <div
        v-else
        class="p-1"
        style="min-height: 35px"
        v-dompurify-html="formField.value"
        @click="handleLinksForDownload($event)"
        :style="{ background: '#eaecee' }"
      ></div>
    </div>
    <div
      class="d-flex align-items-center"
      style="gap: 20px"
      v-if="formField.type === 'file'"
    >
      <FileDragdrop
        title="Upload presentation file"
        label=""
        v-if="formField.type === 'file'"
        warning="Please upload presentation file"
        @upload="(files) => handleFileUpload(files)"
        :uploadTooltipText="
          disabled || formField.disabled ? undefined : 'Upload'
        "
        :disabled="disabled || formField.disabled"
        :upload-button-style="{
          width: 'auto',
          height: '34px',
          paddingLeft: '10px',
          paddingRight: '10px',
          paddingTop: '8px',
          paddingBottom: '10px',
          marginLeft: '10px',
        }"
      />
      <template v-if="formField.value">
        <span
          class="pointer link"
          @click="
            () =>
              $downloadFile({
                query: {
                  file_type: fileType,
                  name: formField.value,
                },
              })
          "
          v-c-tooltip="`Click to download ${formField.value} presentation file`"
        >
          {{ formField.value }}
        </span>
        <Icon
          v-c-tooltip="'Click to delete uploaded file'"
          name="trash"
          @click="handleFileDelete"
        />
      </template>
    </div>
  </div>
  <div v-else :class="wrapperClass">
    <label
      v-if="formField.label"
      :for="formField.name"
      class="text-muted font-weight-bold"
    >
      {{ formField.label }}:
    </label>
    <br />
    <div
      class="p-1"
      style="min-height: 35px"
      v-dompurify-html="formField.value"
      @click="handleLinksForDownload($event)"
      :style="
        formField.disabled && formField.displayOnly ? 'background: #eaecee' : ''
      "
    ></div>
  </div>
</template>

<script>
import { request, uploadFile, handleLinksForDownload } from "@/utils/request";
import { API_ENDPOINTS } from "@/constants/common";
import FileManagement from "./fileManagement/FileManagement";
import { BASE_URL } from "@/constants/config";
export default {
  name: "InputComponent",
  components: {
    FileManagement,
  },
  props: {
    formField: Object,
    incorrectInputValues: {
      type: Boolean,
      default: false,
    },
    disabled: Boolean,
    fileType: Number,
    wrapperClass: {
      type: String,
      default: "",
    },
    appendDropdownToBody: Boolean,
  },
  methods: {
    handleLinksForDownload,
    uploadFile,
    useFilename(filename) {
      this.formField.value += `<p><a href="${BASE_URL}api/files/open/download/?file_id=1&name=${filename}" download="${filename}">[${filename}]</a></p>`;
    },
    selectHandler(item, fieldName) {
      this.$emit("select", item, fieldName);
    },
    removeHandler(item, fieldName) {
      this.$emit("remove", item, fieldName);
    },
    async handleUploadState(xhr, file) {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        const response = JSON.parse(xhr.response || xhr.responseText || {});
        if (response?.detail)
          return alert(response.detail, {
            color: "warning",
            store: this.$store,
          });
      }
      let updateFormField = this.formField;
      updateFormField.value = file.name;
      this.$emit("update:formField", updateFormField);
      this.$emit("upload", file.name);
    },
    handleFileUpload([file]) {
      if (!file) {
        return;
      }
      uploadFile({
        file,
        query: {
          file_type: this.fileType,
        },
        onStateChange: (xhr) => this.handleUploadState(xhr, file),
      });
    },
    async handleFileDelete(file) {
      if (!this.formField.value) return;
      if (
        !(await confirm(
          `Are you sure you want to delete ${this.formField.value}?`
        ))
      ) {
        return;
      }
      let { res } = await request({
        url: API_ENDPOINTS.delete,
        method: "DELETE",
        query: {
          file_type: this.fileType,
          name: this.formField.value,
        },
      });
      if (res.status !== 200) {
        alert("Couldn't delete file.", { color: "danger", store: this.$store });
        return;
      }
      let updateFormField = this.formField;
      updateFormField.value = "";
      this.$emit("update:formField", updateFormField);
      this.$emit("delete", file);
    },
  },
};
</script>

<style lang="scss">
.input-component {
  input::placeholder {
    font-style: italic;
  }

  .mz-select__container {
    width: 100%;
    margin: 0 !important;

    div.mz-options {
      min-width: 100%;
    }
  }

  .vs__selected {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    width: 100%;
    display: block;
    position: absolute;
    left: 0;
    margin-top: 7px;
  }

  .v-select {
    background: white;
  }

  .vs--disabled {
    .vs__dropdown-toggle,
    .vs__search {
      background: none;
      border: none;
    }

    &.v-select {
      background: #eaeaea;
    }
  }

  .vs__actions {
    svg,
    button {
      background: none;
      margin-top: -2px;
    }
  }
}
</style>
